import type { ItemType } from '@/features/items/models/itemSchema';

/** The hardcoded base locale of the SPA — always present and always selected. */
export const DEFAULT_LOCALE = 'en-US';

/** One translation upsert sent to `POST /v1/items/translations`. */
export interface CreateItemTranslationEntry {
  item_id: string;
  language: string;
  name: string;
  description: string;
}

export interface CreateItemTranslationsRequest {
  translations: CreateItemTranslationEntry[];
}

/** `create`: no prior value (current omitted). `update`/`unchanged`: prior exists. */
export type TranslationAction = 'create' | 'update' | 'unchanged';

/** A translation row as returned inside a change's `current`/`new`. The API also
 * sends id/item_type/timestamps; only these fields are read by the recap. */
export interface TranslationRow {
  item_id: string;
  item_code: string;
  language: string;
  name: string;
  description: string;
}

/** Server-computed before (`current`) → after (`new`) for one item/language. The
 * locating fields (item_code, language) live inside the rows, not at the top. */
export interface ItemTranslationChangeResponse {
  action: TranslationAction;
  current?: TranslationRow;
  new: TranslationRow;
}

export interface CreateItemTranslationsResponse {
  dry_run: boolean;
  changes: ItemTranslationChangeResponse[];
}

/** One language with its server-computed translation-completeness. */
export interface LanguageCoverageResponse {
  language: string;
  /** Whether this language is the scope's default. */
  is_default: boolean;
  /** Share of items carrying a non-empty name in the language, 0–100. */
  translated_percent: number;
  /** Items still lacking a name in the language. */
  missing_count: number;
}

export interface GetTranslationLanguagesResponse {
  languages: LanguageCoverageResponse[];
  /** Current default locale for the scope; empty until one is set. */
  default_language: string;
  /** Total items in the scope (denominator for coverage). */
  total_items: number;
}

/** Request body for `POST /v1/languages`. */
export interface CreateLanguageRequest {
  game_code: string;
  sandbox_code: string;
  language: string;
}

/** Returned by `POST /v1/languages` (201). First language in a scope becomes default. */
export interface CreateLanguageResponse {
  id: string;
  language: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/** Request body for `PUT /v1/languages/default`. */
export interface SetDefaultLanguageRequest {
  game_code: string;
  sandbox_code: string;
  language: string;
}

/** Echoed back by `PATCH /v1/languages/default`. */
export interface SetDefaultLanguageResponse {
  game_code: string;
  sandbox_code: string;
  language: string;
}

/** UI-facing language shape produced by the languages query `select`. */
export interface LanguageOption {
  locale: string;
  default: boolean;
  selected?: boolean;
  /** Added this session but not yet persisted server-side. */
  pending?: boolean;
  /** Server-reported coverage, 0–100. Absent for pending (session-added) locales. */
  translatedPercent?: number;
}

/** Server-side filters for the item translations list (URL-backed). Names match
 * the `GET /v1/items` query params, since that is the route now backing the list. */
export interface TranslationsFilters {
  name?: string;
  type?: ItemType | 'all';
  languages?: string[];
}
